<template>

  <main class="event-detail">

    <!-- حالت لودینگ -->
    <div v-if="loading" class="state-box">در حال بارگذاری…</div>

    <!-- پیدا نشد -->
    <div v-else-if="!event" class="state-box">
      <p>این رویداد پیدا نشد یا فعال نیست.</p>
      <router-link class="back-link" to="/special">بازگشت به رویدادهای ویژه</router-link>
    </div>

    <template v-else>

      <!-- بنر رویداد -->
      <section class="hero" :class="{ 'no-image': !event.imageUrl }">
        <img v-if="event.imageUrl" :src="event.imageUrl" :alt="event.title">
      </section>

      <!-- مشخصات -->
      <section class="head">
        <h1>{{ event.title }}</h1>
        <p v-if="event.subtitle" class="subtitle">{{ event.subtitle }}</p>
        <span v-if="dateRange" class="dates">🗓 {{ dateRange }}</span>
      </section>

      <!-- توضیحات -->
      <section v-if="event.description" class="description">
        <p>{{ event.description }}</p>
      </section>

      <!-- مسابقهٔ متصل: جدول و بازی‌ها -->
      <section v-if="event.competition" class="competition-box">
        <router-link
            class="competition-link"
            :to="`/competition/${event.competition.id}`"
        >
          📊 جدول و بازی‌های {{ event.competition.name }}
        </router-link>
      </section>

      <!-- اخبار مرتبط (اگر مسابقهٔ متصل داشته باشد) -->
      <section v-if="news.length" class="news-section">
        <h2>اخبار مرتبط</h2>

        <div class="news-list">
          <router-link
              v-for="n in news"
              :key="n.id"
              class="news-item"
              :to="`/news/${n.slug}`"
          >
            <img v-if="n.imageUrl" :src="n.imageUrl" :alt="n.title" loading="lazy">
            <div class="news-text">
              <span class="news-title">{{ n.title }}</span>
              <span v-if="n.publishedAt" class="news-date">{{ faDate(n.publishedAt) }}</span>
            </div>
          </router-link>
        </div>

        <router-link
            v-if="event.competition"
            class="all-news"
            :to="`/competition/${event.competition.id}`"
        >
          همهٔ اخبار این رویداد
        </router-link>
      </section>

    </template>

  </main>

</template>

<script>
import api from '@/services/api'
import { setSeo } from '@/utils/seo'

export default {

  name: 'SpecialEventDetail',

  data() {
    return {
      event: null,
      news: [],
      loading: true
    }
  },

  computed: {
    dateRange() {
      const ev = this.event
      if (!ev) return ''
      const fmt = d => new Date(d).toLocaleDateString('fa-IR')
      if (ev.startDate && ev.endDate) return `${fmt(ev.startDate)} تا ${fmt(ev.endDate)}`
      if (ev.startDate) return fmt(ev.startDate)
      return ''
    }
  },

  watch: {
    '$route.params.slug'() {
      this.load()
    }
  },

  async created() {
    await this.load()
  },

  methods: {
    faDate(d) {
      return new Date(d).toLocaleDateString('fa-IR')
    },

    async load() {
      this.loading = true
      this.event = null
      this.news = []

      const slug = this.$route.params.slug

      try {
        const ev = await api.request(`/events/${encodeURIComponent(slug)}`)

        if (!ev) {
          this.event = null
          return
        }

        this.event = ev

        setSeo({
          title: `${ev.title} | پوشش ویژه | ضربه آزاد`,
          description: ev.subtitle || `${ev.title}: پوشش اختصاصی رویداد در ضربه آزاد`,
          image: ev.imageUrl || null,
          path: `/special/${ev.slug}`,
        })

        // اخبار مرتبط — اگر رویداد به مسابقه‌ای وصل باشد
        if (ev.competition && ev.competition.id) {
          try {
            const res = await api.request(`/news?competitionId=${ev.competition.id}&limit=12&page=1`)
            this.news = res?.news || []
          } catch (e) {
            console.error('event news:', e)
            this.news = []
          }
        }
      } catch (e) {
        console.error('SpecialEventDetail:', e)
        this.event = null
      } finally {
        this.loading = false
      }
    }
  }

}

</script>

<style scoped lang="scss">

.event-detail {
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  padding: 20px 16px 40px;
  box-sizing: border-box;
}

.state-box {
  padding: 48px 16px;
  text-align: center;
  color: #64748b;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, .05);
}

.back-link {
  display: inline-block;
  margin-top: 10px;
  color: #2563eb;
  text-decoration: none;
  font-weight: 600;
}

.hero {
  border-radius: 18px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 10px 25px rgba(0, 0, 0, .05);
  margin-bottom: 18px;

  img {
    display: block;
    width: 100%;
    max-height: 320px;
    object-fit: cover;
  }

  &.no-image {
    height: 120px;
    background: url("~@/assets/images/header-ball-bg.svg") no-repeat left 4% center / 200px auto, linear-gradient(135deg, #022c22 0%, #064e3b 45%, #047857 85%, #059669 100%);
  }
}

.head {
  margin-bottom: 16px;

  h1 {
    margin: 0 0 6px;
    font-size: 1.5rem;
    color: #0f172a;
  }

  .subtitle {
    margin: 0 0 8px;
    color: #64748b;
    font-size: 1rem;
  }

  .dates {
    display: inline-block;
    font-size: 0.85rem;
    font-weight: 600;
    color: #0f766e;
    background: #ecfdf5;
    border-radius: 999px;
    padding: 4px 12px;
  }
}

.description {
  background: #fff;
  border-radius: 16px;
  padding: 16px 18px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, .05);
  margin-bottom: 16px;

  p {
    margin: 0;
    color: #334155;
    line-height: 1.9;
    white-space: pre-line;
  }
}

.competition-box {
  margin-bottom: 20px;

  .competition-link {
    display: block;
    text-align: center;
    background: #081c15;
    color: #42d392;
    font-weight: 700;
    text-decoration: none;
    border-radius: 14px;
    padding: 14px 16px;
    transition: opacity .2s ease;

    &:hover {
      opacity: .9;
    }
  }
}

.news-section {
  h2 {
    font-size: 1.1rem;
    color: #0f172a;
    margin: 0 0 12px;
  }
}

.news-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 12px;
}

.news-item {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fff;
  border-radius: 14px;
  padding: 10px;
  text-decoration: none;
  box-shadow: 0 6px 16px rgba(0, 0, 0, .05);
  transition: transform .2s ease;

  &:hover {
    transform: translateY(-2px);
  }

  img {
    width: 64px;
    height: 64px;
    border-radius: 10px;
    object-fit: cover;
    flex-shrink: 0;
  }
}

.news-text {
  display: flex;
  flex-direction: column;
  min-width: 0;

  .news-title {
    color: #0f172a;
    font-size: 0.9rem;
    font-weight: 600;
    line-height: 1.6;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .news-date {
    color: #94a3b8;
    font-size: 0.75rem;
    margin-top: 4px;
  }
}

.all-news {
  display: inline-block;
  margin-top: 12px;
  color: #2563eb;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.9rem;
}

/* موبایل — بدون اسکرول افقی */
@media (max-width: 768px) {
  .event-detail {
    padding: 14px 12px 32px;
  }

  .news-list {
    grid-template-columns: 1fr;
  }
}

</style>
